import React, { useEffect } from 'react';
import { useForm, Controller } from 'components/EnhancedReactHookForm';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import Switch from 'components/Switch';
import TextField from 'components/TextField';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import Checkbox from 'components/Checkbox';
import { DEFAULT_DATE_FORMAT } from 'app/constants/DateTimeFormat';
import { addMonths, addYears } from 'date-fns';
import { isEmpty, trim } from 'lodash';
import clsx from 'clsx';
import ColorPicker from './ColorPicker/ColorPicker';
import { REPEAT_ENUM, REPEAT_TYPE, SCHEDULE_TAB_TYPE, CALENDAR_DEFAULT_COLOUR } from '../../constants';

const useStyles = makeStyles(() => ({
  doneLabel: {
    marginLeft: '6px',
    marginRight: '4px',
  },
}));

const AddCalendar = (props) => {
  const { open = false, onCloseDialog = null, scheduleType, date, handleAddCalendar } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const { control, handleSubmit, errors, setValue, watch } = useForm({
    defaultValues: {
      title: '',
      detail: '',
      isEnableRepeat: false,
      colour: CALENDAR_DEFAULT_COLOUR,
      isDone: false,
      repeatType: REPEAT_ENUM.DAY,
      repeatEndDate: addMonths(date, 6),
    },
    mode: 'onBlur',
  });
  const LABEL_SIZE = 120;
  const { repeatType, isEnableRepeat: isRepeat } = watch(['repeatType', 'isEnableRepeat']);

  useEffect(() => {
    if (repeatType === REPEAT_ENUM.YEAR) {
      setValue('repeatEndDate', addYears(date, 1));
    } else {
      setValue('repeatEndDate', addMonths(date, 6));
    }
  }, [repeatType]);

  const handleClose = () => {
    if (onCloseDialog) onCloseDialog();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="sm"
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
      fullWidth
    >
      <DialogTitle className="flex justify-center">
        {t('dashboard:CALENDAR_DIALOG_TITLE', { type: SCHEDULE_TAB_TYPE[scheduleType]?.label })}
        {/* <Icon fontSize="default" className="ml-16">
          {scheduleType?.icon}
        </Icon> */}
      </DialogTitle>
      <DialogContent>
        <form noValidate>
          <Grid container style={{ flexWrap: 'nowrap' }} direction="column" spacing={2}>
            <Grid item>
              <Controller
                as={TextField}
                control={control}
                name="title"
                variant="outlined"
                label={t('dashboard:CALENDAR_TITLE')}
                required
                labelSize={LABEL_SIZE}
                fullWidth
                rules={{
                  validate: {
                    isRequired: (v) => trim(v) !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                  },
                }}
                error={Boolean(errors.title)}
                helperText={errors.title && errors.title.message}
              />
            </Grid>
            <Grid item>
              <Controller
                as={TextField}
                control={control}
                name="detail"
                fullWidth
                variant="outlined"
                label={t('dashboard:CALENDAR_ADD_DETAILS')}
                labelSize={LABEL_SIZE}
                multiline
                rows={4}
                error={Boolean(errors.detail)}
                helperText={errors.detail && errors.detail.message}
              />
            </Grid>
            <Grid item>
              <IconButton disabled>
                <Icon>repeat_oulined</Icon>
              </IconButton>
              <span style={{ fontWeight: 'bold' }}>Repeat</span>
              <Controller as={Switch} control={control} name="isEnableRepeat" color="primary" className="ml-4" />
            </Grid>
            <Grid item container direction="column" justify="flex-start" spacing={1} className="ml-32">
              <Grid item container xs={7} wrap="nowrap">
                <Grid item xs={6}>
                  <Controller
                    as={TextField}
                    control={control}
                    name="repeatInterval"
                    disabled={!isRepeat}
                    labelSize={80}
                    // className="max-w-48"
                    inline
                    rules={{
                      validate: {
                        isValidNumber: (v) =>
                          isEmpty(v) || /^([0-9]+)\.?([0-9]+)?$/g.test(v) || t('VALIDATION_NUMBER_INVALID_MESSAGE'),
                        isRequired: (v) => v !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                      },
                    }}
                    variant="outlined"
                    label="Every"
                    defaultValue={1}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    as={Select}
                    control={control}
                    name="repeatType"
                    // className="w-full ml-8"
                    disabled={!isRepeat}
                    labelSize={5}
                    options={REPEAT_TYPE}
                    inline
                    rules={{
                      validate: {
                        isRequired: (v) => v !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                      },
                    }}
                    variant="outlined"
                  />
                </Grid>
                {errors.repeatInterval && <FormHelperText error>{errors.repeatInterval.message}</FormHelperText>}
              </Grid>
              <Grid item xs={7}>
                <div>
                  <Controller
                    as={DatePicker}
                    control={control}
                    name="repeatEndDate"
                    label="End on"
                    disabled={!isRepeat}
                    inputVariant="outlined"
                    className="w-full"
                    labelSize={80}
                    inline
                    format={DEFAULT_DATE_FORMAT}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid item className="ml-8">
              <Controller as={<ColorPicker permanent />} control={control} name="colour" />
            </Grid>
            <Grid item className="ml-8">
              <Controller
                as={Checkbox}
                name="isDone"
                control={control}
                className={clsx(classes.doneLabel)}
                inline
                label="Done"
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button type="button" className={clsx('mr-8')} onClick={handleClose}>
          {t('BUTTON_CANCEL')}
        </Button>
        <Button color="primary" variant="contained" type="button" onClick={handleSubmit(handleAddCalendar)}>
          {t('BUTTON_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCalendar;
